import { useState } from "react";
import api, { apiError } from "@/lib/api";
import { useDomains } from "@/context/DomainContext";
import { PageHeader, NoDomain } from "@/components/Common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { ShieldOff, Loader2, Search, Trash2 } from "lucide-react";
import { toast } from "sonner";

export default function Permissions() {
  const { selected } = useDomains();
  const did = selected?.id;
  const [fileId, setFileId] = useState("");
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  if (!selected) return <NoDomain />;

  const load = async () => {
    if (!fileId) { toast.error("Enter a Drive file ID"); return; }
    setLoading(true);
    try { const { data } = await api.get(`/domains/${did}/files/${fileId}/permissions`); setResult(data); }
    catch (e) { toast.error(apiError(e)); setResult(null); } finally { setLoading(false); }
  };
  const revoke = async (p) => {
    try { await api.delete(`/domains/${did}/files/${fileId}/permissions/${p.id}`); toast.success("Permission revoked"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const riskTint = (p) => (p.type === "anyone" ? "bg-rose-100 text-rose-700" : p.type === "domain" ? "bg-amber-100 text-amber-700" : "bg-slate-100 text-slate-600");

  return (
    <div className="animate-fade-up">
      <PageHeader title="Permissions & Sharing Audit" subtitle={`${selected.domain} · revoke external access on any Drive file`} icon={ShieldOff} />
      <div className="glass mb-4 rounded-3xl p-5">
        <Label>Drive file ID</Label>
        <div className="mt-1.5 flex gap-2">
          <Input value={fileId} onChange={(e) => setFileId(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load()} placeholder="e.g. 1AbCdEf..." data-testid="permission-file-id-input" />
          <Button onClick={load} disabled={loading} className="glossy-emerald relative rounded-xl" data-testid="audit-permissions-button"><span className="spec-shine" />{loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Search className="mr-1 h-4 w-4" />Audit</>}</Button>
        </div>
      </div>

      {result && (
        <div className="glass rounded-3xl p-5">
          <div className="mb-4">
            <p className="font-heading text-lg font-semibold text-slate-800">{result.file.name}</p>
            <p className="text-xs text-slate-500">Owners: {result.file.owners.join(", ") || "—"}</p>
          </div>
          <div className="space-y-2">
            {result.permissions.map((p) => (
              <div key={p.id} className="flex items-center justify-between rounded-2xl bg-white/60 p-3" data-testid={`permission-${p.id}`}>
                <div>
                  <p className="text-sm font-medium text-slate-800">{p.emailAddress || p.domain || p.displayName || p.type}</p>
                  <div className="mt-1 flex gap-1"><Badge className={riskTint(p)}>{p.type}</Badge><Badge className="bg-teal-50 text-teal-700">{p.role}</Badge></div>
                </div>
                {p.role !== "owner" && (
                  <Button size="sm" variant="outline" className="rounded-lg bg-white/60 text-rose-500" onClick={() => revoke(p)} data-testid={`revoke-permission-${p.id}`}><Trash2 className="mr-1 h-3.5 w-3.5" />Revoke</Button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
